import { createAsyncThunk } from '@reduxjs/toolkit';
import { safetyApi } from '../apis/safety.api';
import { actions } from '../actions/safety.action';
import {
  ReqCreateSafety,
  ReqCreateSafetyByManual,
  ReqCreateSafetySetting,
  ReqUpdateSafety,
  ReqUpdateSafetySetting,
  ReqUpdateSafetyStatus,
  ResSafetyDetail,
  ResSafetyMetas,
  ResSafetySettings
} from '../model/safety.model';
import { ResultBaseProps } from 'modules/common';
import qs from 'qs';
import { RootState } from 'modules/rootReducer';

/**
 * 안전교육 목록 조회
 */
export const fetchSafetyTrainings = createAsyncThunk<ResSafetyMetas, void, { state: RootState }>(
  actions.GET_SAFETIES,
  async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      let request = getState().safety.fetchSafetyTrainingRequest;
      if (request.categoryId === 'ALL') {
        request = {
          ...request,
          categoryId: ''
        };
      }
      const query = qs.stringify(request);

      const response = await safetyApi.getSafeties(query);

      console.log('== response ==  : ', response);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 안전교육 상세 조회
 */
export const fetchSafetyTrainingDetail = createAsyncThunk<
  ResSafetyDetail,
  string,
  { state: RootState }
>(actions.GET_SAFETY_DETAIL, async (safetyId, { getState, fulfillWithValue, rejectWithValue }) => {
  try {
    const state = getState().safety;
    const query = qs.stringify(state.fetchSafetyDetailRequest);

    const response = await safetyApi.getSafetyDetail(safetyId, query);

    return fulfillWithValue(response);
  } catch (error) {
    return rejectWithValue(error);
  }
});

/**
 * 안전 교육 삭제
 */
export const deleteSafety = createAsyncThunk<ResultBaseProps, string>(
  actions.DELETE_SAFETY,
  async (safetyId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await safetyApi.deleteSafety(safetyId);
      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 안전교육 등록
 */
export const createSafety = createAsyncThunk<ResSafetyDetail, void, { state: RootState }>(
  actions.CREATE_SAFETY,
  async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const detail = getState().safety.safetyDetail;

      const request: ReqCreateSafety = {
        categoryId: detail.categoryId,
        trainingId: detail.trainingId,
        trainerId: detail.trainerId,
        trainingDate: detail.trainingDate,
        contents: detail.contents
      };

      const file = detail.file;
      const formData = new FormData();
      formData.append('safety', JSON.stringify(request));

      if (file) {
        formData.append('file', file);
      }

      const response = await safetyApi.createSafety(formData);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 안전교육 수정
 */
export const updateSafety = createAsyncThunk<ResSafetyDetail, void, { state: RootState }>(
  actions.UPDATE_SAFETY,
  async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const detail = getState().safety.safetyDetail;

      const request: ReqUpdateSafety = {
        safetyId: detail.safetyId,
        categoryId: detail.categoryId,
        trainingId: detail.trainingId,
        trainerId: detail.trainerId,
        trainingDate: detail.trainingDate,
        contents: detail.contents
      };

      const file = detail.file;
      const formData = new FormData();
      formData.append('safety', JSON.stringify(request));

      if (file) {
        formData.append('file', file);
      }

      const response = await safetyApi.updateSafety(formData);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 안전교육 상태 변경
 */
export const updateSafetyStatus = createAsyncThunk<ResSafetyDetail, ReqUpdateSafetyStatus>(
  actions.UPDATE_SAFETY_STATUS,
  async (data, { fulfillWithValue, rejectWithValue }) => {
    try {
      const { safetyId, trainerId, ...rest } = data;
      const query = qs.stringify(rest);
      const response = await safetyApi.updateSafetyStatus(safetyId, trainerId, query);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 안전교육 참가자 삭제
 */
export const deleteSafetyAttendance = createAsyncThunk<ResultBaseProps, string>(
  actions.DELETE_SAFETY_ATTENDANCE,
  async (attendanceId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await safetyApi.deleteSafetyAttendance(attendanceId);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 안전교육 참가자 전체 삭제
 */
export const deleteAllSafetyAttendance = createAsyncThunk<ResultBaseProps, string>(
  actions.DELETE_ALL_SAFETY_ATTENDANCE,
  async (safetyId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await safetyApi.deleteAllSafetyAttendance(safetyId);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 안전교육 참가자 추가 ( 다중선택 가능 )
 */
export const createSafetyByManual = createAsyncThunk<ResultBaseProps, ReqCreateSafetyByManual>(
  actions.CREATE_SAFETY_BY_MANUAL,
  async (data, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await safetyApi.createSafetyByManual(data);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 안전교육 세팅 조회
 */
export const fetchSafetySystems = createAsyncThunk<ResSafetySettings, void>(
  actions.GET_SAFETY_SETTING,
  async (_, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await safetyApi.fetchSafetySettings();

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 안전교육 세팅 추가
 * 대분류 추가 6
 * 소분류 추가 optCd1
 */
export const createSafetySetting = createAsyncThunk<ResultBaseProps, ReqCreateSafetySetting>(
  actions.CREATE_SAFETY_SETTING,
  async (request, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await safetyApi.createSafetySetting(request);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 안전교육 세팅 업데이트
 */
export const updateSafetySetting = createAsyncThunk<ResultBaseProps, ReqUpdateSafetySetting>(
  actions.UPDATE_SAFETY_SETTING,
  async (request, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await safetyApi.updateSafetySetting(request);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 안전교육 세팅 삭제
 */
export const deleteSafetySetting = createAsyncThunk<ResultBaseProps, string>(
  actions.DELETE_SAFETY_SETTING,
  async (systemId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await safetyApi.deleteSafetySetting(systemId);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
